import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import EmailVerificationModal from "./EmailVerificationModal";
import LoadingScreen from "./LoadingScreen";
import PlansModal from "./PlansModal";
import CheckoutTransparente from "./CheckoutTransparente";
import { toast } from "sonner";
import DatePicker18 from "./DatePicker18";
import { authFetch, API_BASE_URL } from "../lib/authFetch";
import { validarCPF } from '@/utils/cpfValidator';

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoginSuccess: () => void;
  defaultTab?: string;
}

const LoginModal = ({ open, onOpenChange, onLoginSuccess, defaultTab = "register" }: LoginModalProps) => {
  // Estados para cadastro
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [telefone, setTelefone] = useState('');
  const [ageError, setAgeError] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [autoLoginEmail, setAutoLoginEmail] = useState("");
  const [autoLoginPassword, setAutoLoginPassword] = useState("");
  const [showResendPrompt, setShowResendPrompt] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("Profissional");
  const [showPassword, setShowPassword] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [planoParaPagar, setPlanoParaPagar] = useState<any>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [cpfError, setCpfError] = useState('');

  const navigate = useNavigate();

  const formatCPF = (value: string) => {
    const onlyDigits = value.replace(/\D/g, '');
    return onlyDigits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const validateAge = (date: string) => {
    if (!date) return;
    const today = new Date();
    const birth = new Date(date);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      if (age - 1 < 18) {
        setAgeError('Você deve ter pelo menos 18 anos para se cadastrar.');
        return false;
      }
    } else if (age < 18) {
      setAgeError('Você deve ter pelo menos 18 anos para se cadastrar.');
      return false;
    }
    setAgeError('');
    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAge(birthDate)) return;
    if (!acceptedTerms) {
      toast.error('É necessário aceitar os termos de uso');
      return;
    }
    // Validar CPF
    const cpfLimpo = cpf.replace(/\D/g, "");
    if (!validarCPF(cpfLimpo)) {
      setCpfError('CPF inválido');
      toast.error('CPF inválido. Verifique os dígitos.');
      return;
    }
    setCpfError('');
    setShowLoading(true);
    onOpenChange(false);
    const userData = {
      nome_completo: name,
      cpf: cpfLimpo,
      data_nascimento: birthDate,
      email: registerEmail,
      telefone: telefone,
      password: registerPassword,
      plan: selectedPlan // pode ser "Profissional" por padrão
    };
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData)
      });
      setShowLoading(false);
      if (response.ok) {
        handleRegisterSuccess(userData.email);
      } else {
        const data = await response.json();
        if (response.status === 409 && data.detail === "unverified") {
          setResendEmail(userData.email);
          setShowResendPrompt(true);
        } else {
          // Tratar erros de validação (array) ou mensagem simples (string)
          let errorMsg = "Erro ao cadastrar usuário";
          if (typeof data.detail === "string") {
            errorMsg = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map((e: any) => e.msg || e).join(", ");
          }
          alert(errorMsg);
        }
      }
    } catch {
      setShowLoading(false);
      alert("Erro de conexão com o servidor");
    }
  };

  // Função chamada após cadastro bem-sucedido
  const handleRegisterSuccess = (email: string) => {
    toast.success("Conta criada! Verifique seu e-mail para continuar.");
    setShowLoading(true);
    setTimeout(() => {
      setShowLoading(false);
      setRegisteredEmail(email);
      setShowVerification(true);
      onOpenChange(false);
      setAutoLoginEmail(email);
      setAutoLoginPassword(registerPassword);
      setRegisterEmail("");
      setRegisterPassword("");
      setName("");
      setCpf("");
      setBirthDate("");
      setTelefone("");
      setAgeError("");
    }, 1000);
  };

  // Função chamada após verificação de e-mail bem-sucedida
  const handleVerificationSuccess = async () => {
    setShowVerification(false);
    setShowLoading(true);
    try {
      // Fazer login com as credenciais salvas
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          username: autoLoginEmail,
          password: autoLoginPassword
        })
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.access_token);
        // Inicializar timestamp de início da sessão para controle de expiração
        localStorage.setItem("session_start", Date.now().toString());
        setShowLoading(false);
        setShowPlans(true); // Agora sim, abre o modal de planos após login automático
        // Disparar evento global para atualizar atividades recentes
        window.dispatchEvent(new Event('atividade-registrada'));
      } else {
        setShowLoading(false);
        alert("Erro ao autenticar após verificação. Faça login manualmente.");
      }
    } catch (err) {
      setShowLoading(false);
      alert("Erro de conexão ao autenticar. Faça login manualmente.");
    }
  };

  // Nova função para finalizar o registro após escolha do plano
  const handlePlanChosen = async (plan: string) => {
    setSelectedPlan(plan);
    setShowPlans(false);
    
    // Buscar dados do plano do backend para mostrar no checkout
    try {
      const planosRes = await fetch(`${API_BASE_URL}/planos`);
      const planosData = await planosRes.json();
      const planoSelecionado = (planosData.planos || []).find((p: any) => p.nome === plan);
      
      if (planoSelecionado) {
        // Abre o checkout para pagamento
        setPlanoParaPagar({
          id: planoSelecionado.slug,
          nome: planoSelecionado.nome,
          valor: planoSelecionado.valor,
          descricao: planoSelecionado.descricao || planoSelecionado.nome
        });
        setShowCheckout(true);
      } else {
        toast.error("Plano não encontrado");
      }
    } catch (err) {
      toast.error("Erro ao carregar dados do plano");
    }
  };

  // Função chamada após pagamento bem-sucedido
  const handlePaymentSuccess = async (data: any) => {
    setShowCheckout(false);
    setPlanoParaPagar(null);
    toast.success("Pagamento realizado com sucesso! Bem-vindo!");
    
    // Atualiza a assinatura do usuário
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const subRes = await authFetch(`${API_BASE_URL}/subscriptions/me`);
        if (subRes.ok) {
          const subscription = await subRes.json();
          await authFetch(`${API_BASE_URL}/subscriptions/${subscription.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              plan: selectedPlan,
              status: "active",
              updated_at: new Date().toISOString()
            })
          });
        }
      } catch (err) {
        // Segue o fluxo mesmo com erro
      }
    }
    
    window.location.href = "/dashboard";
  };

  // Função chamada ao cancelar pagamento
  const handlePaymentCancel = () => {
    setShowCheckout(false);
    setPlanoParaPagar(null);
    // Volta para escolha de planos
    setShowPlans(true);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[80vw] max-w-4xl h-[90vh] bg-protec-dark border-protec-gold/30 overflow-y-auto">
        <DialogHeader>
            <DialogTitle className="text-2xl gradient-text flex items-center">
            <Shield className="w-6 h-6" />
            Acesso à Plataforma Protec Bens
          </DialogTitle>
            <DialogDescription className="text-gray-300 ml-6">
              Crie sua conta para acessar as ferramentas de monitoramento
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="block mx-auto flex w-[35vw] bg-[#2D2D2D] border border-[#FFD700] rounded-md p-1">
              <TabsTrigger
                value="register"
                className="flex-1 data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-gray-300 data-[state=inactive]:bg-transparent data-[state=inactive]:text-protec-gold rounded-md transition-colors"
              >
              Registro
            </TabsTrigger>
          </TabsList>

          <TabsContent value="register">
            <Card className="bg-protec-dark-light border-protec-gold/20">
              <CardHeader>
                <CardTitle className="text-white">Criar Conta</CardTitle>
                <CardDescription className="text-gray-300">
                  Registre-se para começar a usar a plataforma (apenas para maiores de 18 anos)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Coluna esquerda */}
                      <div className="flex flex-col gap-4">
                        {/* Nome completo */}
                  <div>
                    <Label htmlFor="register-name" className="text-gray-300">Nome Completo</Label>
                    <Input
                      id="register-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-protec-dark border-protec-gold/30 text-white"
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>
                        {/* CPF */}
                  <div>
                    <Label htmlFor="register-cpf" className="text-gray-300">CPF</Label>
                    <Input
                      id="register-cpf"
                      type="text"
                      value={cpf}
                      onChange={(e) => {
                        const formatted = formatCPF(e.target.value);
                        setCpf(formatted);
                        // Validar CPF em tempo real
                        const cpfLimpo = formatted.replace(/\D/g, '');
                        if (cpfLimpo.length === 11) {
                          if (!validarCPF(cpfLimpo)) {
                            setCpfError('CPF inválido');
                          } else {
                            setCpfError('');
                          }
                        } else {
                          setCpfError('');
                        }
                      }}
                      className={`bg-protec-dark border-protec-gold/30 text-white ${cpfError ? 'border-red-500' : ''}`}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      required
                    />
                    {cpfError && (
                      <p className="text-red-400 text-xs mt-1">{cpfError}</p>
                    )}
                  </div>
                        {/* Data de nascimento */}
                  <div>
                    <Label htmlFor="register-birth" className="text-gray-300">Data de Nascimento</Label>
                          <DatePicker18
                      value={birthDate}
                            onChange={date => {
                              setBirthDate(date);
                              validateAge(date);
                            }}
                    />
                    {ageError && (
                      <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {ageError}
                      </div>
                    )}
                  </div>
                      </div>
                      {/* Coluna direita */}
                      <div className="flex flex-col gap-4">
                        {/* Telefone */}
                        <div>
                          <Label htmlFor="register-phone" className="text-gray-300">Telefone</Label>
                          <Input
                            id="register-phone"
                            type="tel"
                            value={telefone}
                            onChange={(e) => setTelefone(e.target.value)}
                            className="bg-protec-dark border-protec-gold/30 text-white"
                            placeholder="(11) 99999-9999"
                          />
                        </div>
                        {/* E-mail */}
                  <div>
                    <Label htmlFor="register-email" className="text-gray-300">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                            value={registerEmail}
                            onChange={(e) => setRegisterEmail(e.target.value)}
                      className="bg-protec-dark border-protec-gold/30 text-white"
                      placeholder="seu@email.com"
                      required
                    />
                  </div>
                        {/* Senha */}
                  <div>
                    <Label htmlFor="register-password" className="text-gray-300">Senha</Label>
                    <div className="relative">
                    <Input
                      id="register-password"
                        type={showPassword ? "text" : "password"}
                            value={registerPassword}
                            onChange={(e) => setRegisterPassword(e.target.value)}
                        className="bg-protec-dark border-protec-gold/30 text-white pr-10"
                      placeholder="••••••••"
                      required
                    />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-protec-gold transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                        </div>
                      </div>
                  </div>

                  {/* Checkbox de aceitar termos */}
                  <div className="flex items-start gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="accept-terms-register"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-protec-gold/30 bg-protec-dark text-protec-gold focus:ring-protec-gold cursor-pointer"
                    />
                    <label htmlFor="accept-terms-register" className="text-sm text-gray-300">
                      Li e estou de acordo com os{' '}
                      <span 
                        onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
                        className="text-protec-gold hover:underline cursor-pointer"
                      >
                        Termos de Uso
                      </span>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                      className="block mx-auto w-[35vw] bg-gradient-gold text-black font-semibold hover:shadow-gold disabled:opacity-50"
                      disabled={!!ageError || showLoading || !acceptedTerms}
                  >
                    Criar Conta
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <EmailVerificationModal
        open={showVerification}
        email={registeredEmail}
        onSuccess={handleVerificationSuccess}
        onClose={() => setShowVerification(false)}
      />
      <LoadingScreen open={showLoading} />
      <PlansModal 
        open={showPlans}
        subscription={null}
        onClose={() => setShowPlans(false)}
        onPlanChanged={handlePlanChosen}
      />
      
      {/* Modal de Pagamento */}
      {showCheckout && planoParaPagar && (
        <CheckoutTransparente
          plano={planoParaPagar}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      )}

      {showResendPrompt && (
        <Dialog open={showResendPrompt} onOpenChange={setShowResendPrompt}>
          <DialogContent className="bg-protec-dark-light border-protec-gold/20">
            <DialogHeader>
              <DialogTitle className="text-white">Conta não verificada</DialogTitle>
              <DialogDescription className="text-gray-300">
                Já existe uma conta com este e-mail, mas ela ainda não foi verificada.<br />
                Deseja reenviar o código de verificação?
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-4 justify-end mt-4">
              <Button variant="outline" onClick={() => setShowResendPrompt(false)}>
                Cancelar
              </Button>
                    <Button 
                className="bg-protec-gold text-black hover:shadow-gold"
                onClick={async () => {
                  setShowResendPrompt(false);
                  setShowLoading(true);
                  await fetch(`${API_BASE_URL}/resend-code`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: resendEmail })
                  });
                  setShowLoading(false);
                  setRegisteredEmail(resendEmail);
                  onOpenChange(false);
                  setShowVerification(true);
                }}
              >
                Reenviar código
                    </Button>
            </div>
      </DialogContent>
    </Dialog>
      )}

      {/* Modal de Termos de Uso */}
      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white">Termos de Uso</DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh] pr-2 text-gray-300 text-sm leading-relaxed space-y-4">
            <p>Lorem ipsum dolor sit amet. Ut neque deleniti qui facere eaque quo magnam illum. Qui architecto dolorem aut dolor quas est quod numquam et voluptatibus placeat vel maxime labore et molestias nulla At rerum dignissimos. Est facere amet sit consequatur placeat non ipsa tempore aut maxime similique! Et repellat numquam sed accusantium minus id quasi voluptatibus id eaque necessitatibus? Ab nemo maiores sit voluptatum sapiente est sequi omnis et suscipit dolorem. Est molestiae quos eum doloremque facere id enim veritatis a rerum atque. Sed suscipit optio 33 harum itaque vel alias quod. Quo quasi soluta aut atque eveniet vel suscipit rerum. Ex placeat fugiat qui incidunt aperiam et tempore quod aut aliquam doloremque. Nam accusantium pariatur qui quia velit sit sint pariatur qui eaque dolor id recusandae excepturi. Qui eius optio et nisi deleniti est exercitationem fugiat ea nisi repellendus qui vero quidem et reiciendis aliquid.</p>
            
            <p>Aut natus reprehenderit et obcaecati Quis ad reiciendis voluptatem aut Quis fugit nam dolor quia. Cum culpa magni quo iste quaerat nam laboriosam quibusdam sed porro ratione et sint quis est quas ipsam! Est laborum perferendis non optio ipsum ut nesciunt architecto et consequuntur Quis sit dolorem nihil. Hic incidunt dolor est omnis suscipit et earum nulla et porro maiores? Ut maiores consequuntur ut facere dolores sed quasi blanditiis nam tenetur distinctio sit temporibus nihil et assumenda dolorem a enim eius. Est omnis consequatur qui explicabo delectus et tenetur galisum et tempore voluptas sed magnam ipsam 33 assumenda minima. In rerum voluptas quo necessitatibus rerum sit veritatis animi eum consequatur quam cum cupiditate perspiciatis. Sed necessitatibus omnis eos sunt quia et asperiores voluptas sit galisum quasi est nesciunt explicabo et nisi incidunt.</p>
            
            <p>Ut totam pariatur sed cupiditate libero qui obcaecati praesentium et voluptas quasi sed assumenda ullam et quia sint. Est necessitatibus dolorem eum quidem culpa et galisum totam vel eaque doloremque? Est internos quisquam est quod necessitatibus sed autem quidem id dolorem voluptas. Quo omnis asperiores et impedit architecto est saepe tenetur in voluptate nihil qui nesciunt nesciunt est commodi debitis. Est rerum perspiciatis qui architecto consequatur aut obcaecati voluptatibus qui maxime voluptates et earum velit et quia doloribus qui quia odio? Qui quisquam dignissimos qui itaque deserunt et beatae illum ad enim molestiae. Aut iusto quia non cumque sint ea doloremque numquam ut voluptatem earum eum accusamus voluptas. Et quasi aperiam ut laboriosam necessitatibus et amet adipisci et quia odio.</p>
          </div>

          <div className="flex justify-end pt-4">
            <Button 
              onClick={() => setTermsModalOpen(false)}
              className="bg-protec-gold text-black hover:bg-protec-gold-light"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginModal;
